import Dialog from "ui/elements/dialog/Dialog";
import releaseFormView from "ui/editor/dialogs/release/release-form.handlebars";
import Template from "ui/Template";
import Form from "ui/elements/form/Form";
import {
	LIMIT_SCENE_DESC_MAX_LENGTH,
	LIMIT_SCENE_NAME_MAX_LENGTH,
	LIMIT_USERNAME_MAX_LENGTH, LIMIT_USERNAME_MIN_LENGTH,
	validateEmail, validateSceneDesc,
	validateSceneName,
	validateUserName
} from "ui/helpers/validators";
import MainDb, {STORE_CONFIG} from "ui/editor/db/MainDb";
import {i18n} from "ui/i18n";
import Ajax from "ui/helpers/Ajax";
import FatalError from "ui/components/fatal-error/FatalError";
import Spinner from "ui/components/spinner/Spinner";
import EmailCode from "ui/dialogs/email-code/EmailCode";
import {CurrentUser} from "ui/CurrentUser";

/**
 * @type {Template}
 */
let template = null;

/**
 * @typedef {Object} T_ReleaseData
 * @property {string} name
 * @property {string} desc
 * @property {boolean} confirm1
 * @property {boolean} confirm2
 */

export default class ReleaseForm extends Dialog {
	/**
	 * @param {string} sceneId
	 * @param {Canvas} canvas
	 */
	constructor(sceneId, canvas) {
		super({
			title: i18n('title.release'),
			buttons: [
				{
					caption: i18n('buttons.save-release'),
					role: 'release',
					icon: '/images/icons.svg#icon-svg-release'
				},
				{
					caption: i18n('buttons.close'),
					role: 'close'
				}
			]
		});

		if (template === null) {
			template = new Template(releaseFormView);
		}

		/**
		 * @type {string}
		 * @private
		 */
		this._sceneId = sceneId;

		/**
		 * @type {Canvas}
		 * @private
		 */
		this._canvas = canvas;

		/**
		 * @type {Form}
		 * @private
		 */
		this._form = null;

		/**
		 * @type {?function(data:(T_ReleaseData|null))}
		 * @private
		 */
		this._onComplete = null;

		/**
		 * @type {MainDb}
		 * @private
		 */
		this._db = MainDb.getInstance();

		void this._buildForm();
	}

	/**
	 * @returns {Promise<T_ReleaseData|null>}
	 */
	getResultPromise() {
		return new Promise((resolve) => {
			this._onComplete = resolve;
		});
	}

	/**
	 * @override
	 * @param {string} role
	 * @returns {void}
	 * @protected
	 */
	_handleButton(role) {
		super._handleButton(role);

		if (role === 'release') {
			let data = this._form.getValues();
			if (data !== null) {
				this._apply(data).then();
			}
		}
	}

	/**
	 * @private
	 * @param {T_ReleaseData} data
	 * @returns {void}
	 */
	async _apply(data) {
		/*if (!data.confirm1) {
			this._form.showError(i18n('errors.input.confirm'), ['confirm1']);
			return;
		}

		if (!data.confirm2) {
			this._form.showError(i18n('errors.input.confirm'), ['confirm2']);
			return;
		}*/

		this._canvas.updateProps({
			name: data.name,
			desc: data.desc,
		});

		if (this._onComplete) {
			this._onComplete(data);
			this._onComplete = null;
			this.destroy();
		}
	}

	/**
	 * @param {T_ReleaseData} data
	 * @return {Promise<boolean>}
	 * @private
	 */
	async _requestReleaseToken(data) {
		Spinner.show();

		let res;
		try {
			res = await Ajax.post('/editor/pre-release/' + this._sceneId, {
				id: this._sceneId,
				author: data.author,
				email: data.email,
				name: data.name,
				desc: data.desc
			});

			if (res.result === 'ok') {
				Spinner.hide();
				data.token = '';
				return true;
			}

			if (res.result !== 'check') {
				this._form.showError(res.error, res.fields || []);
				Spinner.hide();
				return false;
			}

			data.token = res.token;
		} catch (e) {
			FatalError.show(typeof e === 'string' ? e : i18n('errors.http.error'));
			Spinner.hide();
			return false;
		}

		return true;
	}

	/**
	 * @param {T_ReleaseData} data
	 * @return {Promise<boolean>}
	 * @private
	 */
	async _requestReleaseCode(data) {
		if (data.token === '') {
			return true;
		}

		const code = await new EmailCode({
			url: '/editor/check-code/' + this._sceneId,
			payload: {
				id: this._sceneId,
				author: data.author,
				email: data.email,
				token: data.token,
			}
		}).getResultPromise();

		if (code === '') {
			return false;
		}

		data.code = code;

		return true;
	}

	/**
	 * @private
	 * @returns {Promise<void>}
	 */
	async _buildForm() {
		const props = this._canvas.getProps();
		const el = template.createElement({});

		this._form = new Form({
			parent: el,
			onApply: this._apply.bind(this),
			values: {
				name: props.name,
				desc: props.desc,
			},
			fields: [
				{
					type: 'text',
					name: 'name',
					caption: i18n('inputs.scene.name'),
					required: true,
					fullWidth: true,
					maxlength: LIMIT_SCENE_NAME_MAX_LENGTH,
					validators: [validateSceneName]
				},
				{
					type: 'textarea',
					name: 'desc',
					caption: i18n('inputs.scene.desc'),
					validators: [validateSceneDesc],
					maxlength: LIMIT_SCENE_DESC_MAX_LENGTH,
				},
				/*{
					type: 'checkbox',
					name: 'confirm1',
					caption: i18n('inputs.release.confirm1'),
				},
				{
					type: 'checkbox',
					name: 'confirm2',
					caption: i18n('inputs.release.confirm2'),
				},*/
			]
		});
		this.setContent(el);
	}

	destroy() {
		if (this._onComplete !== null) {
			this._onComplete(null);
			this._onComplete = null;
		}
		super.destroy();
	}
}
